import {CommonDropDown, CommonProps} from '@hconnect/common/components/shiftEventFormFields'
import {
  statusList,
  statusListApprovalStates,
  StatusListWithApproverStates,
  ShiftEvent
} from '@hconnect/common/types'
import {useRequiredUser} from '@hconnect/login'
import {EventStatusLabel} from '@widgets/common-shift-event'
import React, {useMemo} from 'react'

export const shouldDisableStatusField = (
  isApprovalNeeded: boolean,
  isOwner: boolean,
  isApproved?: boolean
) => isApprovalNeeded && !(isApproved && isOwner)

type GetExcludedOptionsParams = {
  isApprovalNeeded: boolean
  isPermanent: boolean
  isReverted: boolean
}

export const getExcludedOptions = ({
  isApprovalNeeded,
  isPermanent,
  isReverted
}: GetExcludedOptionsParams): StatusListWithApproverStates[] => {
  // when approval is not needed, we return an empty array as there are no exclusions in that case
  if (!isApprovalNeeded) return []

  if (isReverted) {
    return ['approved', 'rejected', 'waitingForApproval', 'implementedTemporary', 'implemented']
  }

  const implementedStatusToExclude = isPermanent ? 'implementedTemporary' : 'implemented'
  return ['approved', 'rejected', 'waitingForApproval', implementedStatusToExclude, 'reverted']
}

export const ParameterChangeStatusDropDown = ({
  eventData,
  onChange,
  errorText,
  disabled,
  ...rest
}: CommonProps<StatusListWithApproverStates> & {
  eventData: ShiftEvent
  errorText?: string
}): React.ReactElement => {
  const {id: userId} = useRequiredUser()
  const {status: value, createdBy} = eventData
  const isApprovalNeeded = 'needsApproval' in eventData && eventData.needsApproval

  const isPermanent = !('schedule' in eventData && eventData.schedule)
  const isOwner = createdBy ? createdBy.id === userId : true
  const isApproved = !['waitingForApproval', 'rejected'].includes(value)
  const isReverted = ['reverted'].includes(value)
  const options = useMemo(() => {
    if (isApprovalNeeded) {
      return statusListApprovalStates
    }
    return statusList
  }, [isApprovalNeeded])
  const disableStatusField = shouldDisableStatusField(isApprovalNeeded, isOwner, isApproved)

  const excluded = useMemo<StatusListWithApproverStates[]>(
    () =>
      getExcludedOptions({
        isApprovalNeeded,
        isPermanent,
        isReverted
      }),
    [isApprovalNeeded, isPermanent, isReverted]
  )

  return (
    <CommonDropDown<StatusListWithApproverStates>
      {...rest}
      disabled={disableStatusField || disabled}
      data-test-id="event-form-status"
      errorText={errorText}
      onChange={onChange}
      required
      value={value as StatusListWithApproverStates}
      hiddenOptions={excluded}
      keyName="status"
      options={options}
      customTemplate={(status) => {
        return <EventStatusLabel status={status} />
      }}
    />
  )
}
